/** @jsx jsx */
import React, {
	type ReactNode,
	type ComponentType,
	type ElementRef,
	type KeyboardEvent,
	type MouseEventHandler,
	type CSSProperties,
} from 'react';
import { styled as styled2, css as compiledCSS, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import { token } from '@atlaskit/tokens';
import { draggingMixin } from '@atlassian/jira-common-styles/src/dragging.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { InlineErrorMessageWrapper } from '@atlassian/jira-project-settings-apps-common/src/inline-error-message/view.tsx';
import {
	LAYOUT_ITEM_HIGHLIGHTED_TRANSITION_PARAMS,
	layoutItemNotDraggingMixin,
} from '../styled.tsx';

export const fieldBorderRadius = '3px';
const FIELD_ANIMATION_DURATION = 250;

const placeholderColor = token('color.text.subtlest');
const padding = gridSize;
export const verticalMargin = gridSize * 2;
const border = gridSize / 4;
const elementHeight = gridSize * 3;
const animationDuration = `${FIELD_ANIMATION_DURATION}ms`;
const animationParams = `${animationDuration} cubic-bezier(0.15, 1, 0.3, 1)`;
export const cardHeight: number = 2 * padding + 2 * border + elementHeight;
export const optionsHorizontalPadding: number = elementHeight + gridSize; // MC-806
export const cardBodyPadding = 5 * gridSize;

/* Margins!
 * Must be:
 * + bottom because if it's up + bottom then different margin collapsing rules apply for Draggables and placeholders
 *
 * If you change these make sure that D&D still works. Moving items between lists, reordering, and aborting the drag
 * should not cause visible jumps, all should animate back to its please seamlessly.
 */

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ElevationMargin = styled.div<{
	children?: ReactNode;
	innerRef?: (ref: ElementRef<'div'> | null) => void;
}>({
	marginBottom: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginBottom: 0,
	},
});

export const Margin = ({
	innerRef,
	children,
	...rest
}: {
	children?: ReactNode;
	innerRef?: (ref: ElementRef<'li'> | null) => void;
	tabIndex?: number;
	style?: CSSProperties | undefined;
}) => (
	<li
		ref={innerRef}
		{...rest}
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css={compiledCSS({
			marginBottom: token('space.050'),
			'list-style': 'none',
			'margin-top': 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:last-child': {
				marginBottom: 0,
			},
		})}
	>
		{children}
	</li>
);

const hoverMixin = `
    &:hover {
        background-color: ${token('color.background.input.hovered')};
    }
`;

const shrinks = `
    min-width: 0;
    flex-shrink: 1;
    overflow-x: hidden;
    overflow-y: hidden;
    text-overflow: ellipsis;
`;

/* stylelint-disable no-descending-specificity */
// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FieldHeaderContainer = styled.div<{
	onClick?: MouseEventHandler<HTMLDivElement> | undefined;
}>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	padding: `${padding}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${border}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginRight: `${padding}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > *:last-child,[data-isEditable][data-testid="project-settings-apps-issue-type-page.field-item-navigation-dropdown-menu.dropdown-menu--trigger"]':
		{
			marginRight: 0,
		},
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LozengeContainer = styled.div<{
	children?: ReactNode;
}>({
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		verticalAlign: "'text-bottom !important'",
	},
});

// `& > :first-child` - when not expanded, we should not display the child elements
// `& > div > :first-child` - first item should have additional margin at the top
// `& > div > :first-child:last-child` - it will remove additional margin in case if FieldContentAnimation has only one child (Footer)
// (for example: priority field without default value should not have additional margin at the top)
// `div` in the middle of the selector is a wrapper created by HeightAnimator
// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FieldContentAnimation = styled.div<{
	isExpanded: boolean;
	isAnimating: boolean;
	children: ReactNode;
	height: number;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>((props) => ({
	transition: `height ${animationParams}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: props.isExpanded ? `${props.height}px` : '0px',

	/* We only want to hide overflow during height animation, otherwise popups inside field configuration will also be
    hidden (e.g. select dropdowns) */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	overflow: props.isAnimating ? 'hidden' : undefined,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > div > :first-child': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		paddingTop: props.isExpanded ? `${verticalMargin - padding}px` : undefined,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: props.isExpanded ? undefined : 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > div > :first-child:last-child': {
		marginTop: 0,
	},
}));

/* stylelint-enable no-descending-specificity */

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconContainer = styled.span<{
	children?: ReactNode;
}>({
	marginRight: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${elementHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${elementHeight}px`,
	borderRadius: fieldBorderRadius,
	textAlign: 'center',
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FieldStatusIconContainer = styled2.span({
	display: 'inline-flex',
	marginLeft: token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Form = styled2.form<{
	children?: ReactNode;
}>({
	margin: 0,
	width: '100%',
});

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ErrorMessageLabel = styled.div<{
	children?: ReactNode;
	htmlFor: string;
}>`
	padding-right: ${optionsHorizontalPadding}px;
	padding-left: ${optionsHorizontalPadding + gridSize}px;
	margin-bottom: ${verticalMargin}px;

	> ${InlineErrorMessageWrapper} {
		margin-top: 0;
	}
`;

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InfoMessageWrapper = styled.div<{
	children?: ReactNode;
}>({
	padding: `0 ${token('space.500')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginBottom: `${verticalMargin}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InfoMessageNotDraggable = styled2.div<{
	children?: ReactNode;
}>({
	cursor: 'initial',
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Footer = styled.div<{
	children?: ReactNode;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		padding: `${padding}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderTop: `2px solid ${token('color.border')}`,
		display: 'flex',
		justifyContent: 'space-between',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	shrinks,
);

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AkFieldWrapper = styled.div<{
	children?: ReactNode;
	onKeyDown?: (arg1: KeyboardEvent<HTMLDivElement>) => void;
}>({
	cursor: 'text',
	flexGrow: 1,
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& label > div': {
		paddingTop: 0,
	},
	/* The Field component from @atlaskit/form has a built-in margin-top which conflicts with our existing */
	/* spacing. We could adopt this spacing everywhere but there are some components which don't use Field */
	/* (e.g. LabelsPicker), so overriding the Field component margin here is the path of least resistance. */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		marginTop: 0,
	},
});

/* stylelint-disable no-descending-specificity */
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line  @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const PullRight = styled.section<{
	children?: ReactNode;
}>`
	display: flex;
	flex-wrap: nowrap;
	flex-direction: row;
	justify-content: flex-end;
	flex: 1 1 auto;
	min-width: 0;
	align-items: center;

	& > * {
		margin-left: ${token('space.100')};
		margin-right: ${token('space.100')};
		${shrinks};
	}
	& > *:last-child {
		margin-right: 0;
	}
`;

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ChevronContainer = styled.span<{
	children?: ReactNode;
	isOpen: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>((props) => ({
	cursor: 'pointer',
	display: 'flex',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${elementHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${3 * gridSize}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button > span': {
		transition: `transform ${animationParams}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		transform: props.isOpen ? 'rotate(90deg)' : undefined,
	},
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const HiddenSubmitInput = styled2.input<{
	children?: ReactNode;
	type: string;
}>({
	display: 'none',
});

const elevationCollapsed = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	maxHeight: '0 !important',
	overflow: 'hidden',
	padding: 0,
	opacity: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	marginBottom: `${-gridSize / 2}px`,
});

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const sortableMixin = css<{ isOpen: boolean; isDragging?: boolean }>`
	${(props) => (props.isOpen ? '' : hoverMixin)};
	${(props) => {
		if (props.isDragging) {
			return draggingMixin;
		}

		return layoutItemNotDraggingMixin;
	}};
`;

const unsortableHoverMixin = css({
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.input.hovered'),
	},
});

// FIXME: type error passing result of `css` to `css`
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const clickableMixin = css`
	${unsortableHoverMixin};
	cursor: pointer;
`;

const unsortableOrLockedMixin = css({
	borderWidth: '1px',
	borderStyle: 'solid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	borderColor: `${token('elevation.shadow.overflow.perimeter')}`,
});

// FIXME: type error passing result of `css` to `css`
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const unsortableMixin = css<{ isOpen: boolean }>`
	${unsortableOrLockedMixin}
	${(props) => (props.isOpen ? '' : clickableMixin)};
`;
// FIXME: type error passing result of `css` to `css`
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const lockedMixin = css`
	${unsortableOrLockedMixin}
	cursor: not-allowed;
`;

type ElevationProps = {
	sortable: boolean;
	isExpandable: boolean;
	isOpen: boolean;
	deleteAnimation: boolean;
	isHighlighted: boolean;
};

// FIXME: convert-props-syntax doesn't catch the `background-color` usage
// @ts-expect-error - TS2345: Argument of type 'StyledComponentClass'
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Elevation = styled<ElevationProps>(ElevationMargin)(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => ({
		width: '100%',
		boxSizing: 'border-box',
		borderRadius: fieldBorderRadius,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundColor: props.isHighlighted
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.discovery.hovered')
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.input'),
		opacity: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		transition: `max-height ${animationParams}, padding ${animationParams}, margin ${animationParams}, opacity ${animationParams}, box-shadow ${animationParams}, background-color ${LAYOUT_ITEM_HIGHLIGHTED_TRANSITION_PARAMS}`,
	}),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.sortable ? sortableMixin : ''),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (!props.sortable && props.isExpandable ? unsortableMixin : ''),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (!props.sortable && !props.isExpandable ? lockedMixin : ''),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.isOpen ? '' : 'overflow: hidden;'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => (props.deleteAnimation ? elevationCollapsed : ''),
	{
		'&:focus': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			boxShadow: `0 0 0 2px ${token('color.background.selected')}`,
			outline: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		input: {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&::-webkit-input-placeholder': {
				/* WebKit, Blink, Edge */
				color: placeholderColor,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&::-moz-placeholder': {
				/* Mozilla Firefox 19+ */
				color: placeholderColor,
				opacity: 1,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&::-ms-input-placeholder': {
				/* Microsoft Edge */
				color: placeholderColor,
			},
		},
	},
);

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const InlineInputWrapper = styled.div({
	/* Double ampersand to override an AK style with higher specificity */
	/* https://styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&& [data-ds--text-field--input]': {
		padding: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${elementHeight}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-ds--text-field--container]': {
		border: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface.raised'),
	},
});

// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FieldConfigurationDefaultValue = styled.div<{
	children?: ReactNode;
	['data-test-cypress']?: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingRight: `${cardBodyPadding}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${cardBodyPadding}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginBottom: `${verticalMargin}px`,
	display: 'flex',
	flex: 1,
	flexDirection: 'row',
});

// This component has slightly less left-padding so that the checkboxes are left aligned with content above/below
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const CheckboxFieldConfigurationDefaultValue: ComponentType<{
	children?: ReactNode;
	['data-test-cypress']?: string;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(FieldConfigurationDefaultValue)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${cardBodyPadding - 5}px`,
});
